import React, { useContext, useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { Tooltip, Typography, Box, Collapse, Chip } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { hexToPastel } from '../../../../../../utils/HexToPastelUtil';
import { getFlowGroupDisplay } from '../../../../../../utils/flowGroupCanvasUi';
import { CanvasContext } from '../../../WorkflowCanvas';
import useProjectStore from '../../../../../ProjectCanvas/useProjectStore';
import useCanvasStore from '../../../useCanvasStore';
import useZoomCollapse from '../../../../../../hooks/useZoomCollapse';
import { LoopHandles } from './ComponentNodeUtil/LoopHandles';
import AddConditionalPathButton from './ComponentNodeUtil/AddConditionalPathButton';
import { TYPES_WITH_CREATION_PORT, getTriggerLabels } from '../../../ReactFlowUtil/creationPortLabels';
import './ComponentNode.css';

const BRANCH_PATH_TYPE = 'branch_paths';

// Both output ports live on the right edge: the submission port stays at the
// default vertical position, and the creation port sits directly below it.
// Constants kept so each handle dot and its on-node label move in lockstep.
const PORT_SUBMISSION_TOP = 20;
const PORT_CREATION_TOP = 52;

const NodeCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'flowAccent',
})(({ theme, flowAccent }) => ({
  width: 520,
  borderRadius: 8,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${
    flowAccent
      ? alpha(flowAccent, theme.palette.mode === 'dark' ? 0.48 : 0.34)
      : theme.palette.divider
  }`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflow: 'visible',
  position: 'relative',
}));

const NodeHeader = styled('div', {
  shouldForwardProp: prop => prop !== 'bgcolor' && prop !== 'collapsed' && prop !== 'squareTop',
})(({ bgcolor, collapsed, squareTop }) => ({
  position: 'relative',
  padding: collapsed ? '14px 18px' : '8px 12px',
  backgroundColor: bgcolor,
  borderRadius: (() => {
    if (collapsed && squareTop) return '0 0 8px 8px';
    if (collapsed) return 8;
    if (squareTop) return '0 0 0 0';
    return '8px 8px 0 0';
  })(),
  display: 'flex',
  flexDirection: 'column',
  gap: collapsed ? 4 : 2,
  transition: 'padding 0.2s ease, border-radius 0.2s ease, gap 0.2s ease',
}));

const portStyles = (theme, side, { top = 20 } = {}) => ({
  '&&': {
    position: 'absolute',
    [side]: -7,
    top,
    transform: side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)',
    width: 14,
    height: 14,
    backgroundColor: theme.palette.text.secondary,
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: '50%',
    zIndex: 10,
    cursor: 'crosshair',
    transition: 'transform 0.1s, background-color 0.1s',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -14,
      left: -14,
      right: -14,
      bottom: -14,
      borderRadius: '50%',
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      width: 20,
      height: 20,
      transform: side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)',
      transition: 'all 0.1s',
    },
  },
});

const PortLeft = styled(Handle)(({ theme }) => portStyles(theme, 'left'));
const PortRight = styled(Handle)(({ theme }) => portStyles(theme, 'right'));
// Creation port: anchored to the right edge directly below the submission
// port so both output ports sit together on the right. Edges route rightward.
const PortRightCreation = styled(Handle)(({ theme }) =>
  portStyles(theme, 'right', { top: PORT_CREATION_TOP })
);

// Trigger hints rendered next to each output port when a node exposes more
// than one (review_task today). Positioned outside the card on the top edge
// so adjacent nodes (typical left-to-right and downward flow) can't visually
// occlude them. Each label is hidden the moment its port has an outgoing
// edge — the edge itself carries the matching trigger label, so showing both
// at once would double up.
const PortLabelBase = styled('span')(({ theme }) => ({
  position: 'absolute',
  fontSize: 11,
  fontWeight: 500,
  color: theme.palette.text.secondary,
  opacity: 0.7,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 9,
}));

// Both labels sit to the right of their right-edge port dots, the creation
// label below the submission label. Both hide via `has*Edge` once wired —
// the edge chip already carries the trigger phrase, so showing both would
// duplicate the text.
const PortLabelCreation = styled(PortLabelBase)({
  top: PORT_CREATION_TOP,
  left: '100%',
  transform: 'translate(22px, -50%)',
});

const PortLabelSubmission = styled(PortLabelBase)({
  top: PORT_SUBMISSION_TOP,
  left: '100%',
  transform: 'translate(22px, -50%)',
});

/**
 * InlineNodeView replaces the old ComponentNodeView when inline editing is active.
 * Renders a clean card matching the NodeUI preview: colored header + config panel below.
 */
const InlineNodeView = ({
  data,
  nodeId,
  saving,
  addLinkedConditionalPath,
  children,
  readOnly = false,
  selected = false,
}) => {
  const theme = useTheme();
  const zoomCollapsed = useZoomCollapse();
  const { collapseNodes } = useContext(CanvasContext);
  const collapsed = (zoomCollapsed || collapseNodes) && !selected;
  const { hasInput, hasOutput, type, isTemp, baseColor, logo, label, displayName, description, schemaVersion } = data;

  const isAiActive = useCanvasStore(s => s.aiActiveComponentIds.has(nodeId));
  const components = useProjectStore(s => s.components);
  const showCreationPort = hasOutput && TYPES_WITH_CREATION_PORT.has(type);
  const triggerLabels = showCreationPort ? getTriggerLabels(type) : null;

  // When the user wires either output, the edge itself carries the trigger
  // label (see Link.js → TriggerEdgeLabel), so we collapse the on-node hint
  // for that port to avoid duplicating the same text twice. The selectors
  // return booleans so InlineNodeView only re-renders when the answer flips.
  const hasCreationEdge = useCanvasStore(s =>
    showCreationPort
      ? Boolean(s.edges?.some(e => e.source === nodeId && e.sourceHandle === 'source-creation'))
      : false
  );
  const hasSubmissionEdge = useCanvasStore(s =>
    showCreationPort
      ? Boolean(
          s.edges?.some(
            e =>
              e.source === nodeId &&
              e.type === 'link' &&
              (e.sourceHandle === 'source-completion' || e.sourceHandle == null),
          ),
        )
      : false
  );

  const flowStrip = useMemo(
    () => getFlowGroupDisplay(components, nodeId),
    [components, nodeId]
  );

  const headerBg = useMemo(
    () => hexToPastel(baseColor || '#808080', theme.palette.mode),
    [baseColor, theme.palette.mode]
  );

  return (
    <NodeCard
      className={isAiActive ? 'ai-active-glow' : undefined}
      flowAccent={flowStrip?.accent ?? null}
    >
      {flowStrip && (
        <Box
          sx={{
            px: 1.25,
            py: 0.6,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            borderLeft: `3px solid ${flowStrip.accent}`,
            backgroundColor: (t) => alpha(flowStrip.accent, t.palette.mode === 'dark' ? 0.12 : 0.08),
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderBottom: (t) => `1px solid ${alpha(flowStrip.accent, t.palette.mode === 'dark' ? 0.28 : 0.2)}`,
          }}
        >
          <AccountTreeOutlinedIcon sx={{ fontSize: 16, color: flowStrip.accent, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary',
            }}
            title={flowStrip.title}
          >
            {flowStrip.title}
          </Typography>
          <Chip
            label={flowStrip.isEntry ? 'Flow entry' : `${flowStrip.memberCount} in group`}
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 600,
              flexShrink: 0,
              backgroundColor: alpha(flowStrip.accent, 0.18),
              color: 'text.primary',
              border: 'none',
              '& .MuiChip-label': { px: '6px' },
            }}
          />
        </Box>
      )}

      {/* Left handle (input) */}
      {hasInput && (
        <PortLeft type="target" position={Position.Left} isConnectable={!isTemp} />
      )}

      {/* Right handle (output) — completion / submission port. Always at
          the default right-edge vertical position. When a creation port is
          also exposed we tag this one explicitly so existing edges keyed
          on `source-completion` keep round-tripping; other types omit the
          id so legacy edges with `sourceHandle === null` still resolve. */}
      {hasOutput && !showCreationPort && (
        <PortRight type="source" position={Position.Right} isConnectable={!isTemp} />
      )}
      {hasOutput && showCreationPort && (
        <PortRight
          type="source"
          position={Position.Right}
          id="source-completion"
          isConnectable={!isTemp}
        />
      )}

      {/* Second source handle — fires the moment the component enters
          WAITING. Anchored to the right edge directly below the submission
          port so both output ports sit together on the right. Only rendered
          for types in TYPES_WITH_CREATION_PORT. Labels are looked up by type
          so each wait-state component speaks its own vocabulary (Create Tasks
          talk about submissions, agent components talk about session lifecycle). */}
      {showCreationPort && triggerLabels && (
        <>
          {!hasCreationEdge && <PortLabelCreation>{triggerLabels.creation}</PortLabelCreation>}
          {!hasSubmissionEdge && (
            <PortLabelSubmission>{triggerLabels.submission}</PortLabelSubmission>
          )}
          <Tooltip
            title={`Fires ${triggerLabels.creation.toLowerCase()} — connect to send Slack/SMS, call a webhook, etc., without waiting for completion.`}
            placement="top"
            arrow
          >
            <PortRightCreation
              type="source"
              position={Position.Right}
              id="source-creation"
              data-port="creation"
              isConnectable={!isTemp}
            />
          </Tooltip>
        </>
      )}

      {/* Branch path / conditional path button (hidden in readOnly) */}
      {!readOnly && hasOutput && type === BRANCH_PATH_TYPE && (
        <AddConditionalPathButton
          disabled={saving}
          onClick={addLinkedConditionalPath}
          classes={{
            addPathButton: 'inline-add-path-button',
            addPathIcon: 'inline-add-path-icon',
            addPathTooltipTitle: 'inline-add-path-tooltip',
          }}
          data={data}
        />
      )}

      <LoopHandles />

      {/* Colored header: component name + type + description */}
      <NodeHeader bgcolor={headerBg} collapsed={collapsed} squareTop={!!flowStrip}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: collapsed ? '10px' : '6px' }}>
          {logo && (
            <img
              src={logo}
              alt=""
              width={collapsed ? 32 : 20}
              height={collapsed ? 32 : 20}
              style={{ marginTop: 1, transition: 'width 0.2s ease, height 0.2s ease' }}
            />
          )}
          <Box sx={{ overflow: 'hidden', flex: 1 }}>
            <Typography
              sx={{
                fontSize: collapsed ? 22 : 13,
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                transition: 'font-size 0.2s ease',
              }}
              title={label || displayName || type}
            >
              {label || displayName || type}
            </Typography>
            <Typography
              sx={{
                fontSize: collapsed ? 16 : 11,
                color: 'text.secondary',
                transition: 'font-size 0.2s ease',
              }}
            >
              {displayName || type}
            </Typography>
          </Box>
        </Box>
        {description && (
          <Typography
            sx={{
              fontSize: collapsed ? 15 : 11,
              color: 'text.disabled',
              ml: collapsed ? '42px' : '26px',
              transition: 'font-size 0.2s ease, margin-left 0.2s ease',
            }}
          >
            {description}
          </Typography>
        )}
        <Chip
          label={schemaVersion >= 2 ? 'v2' : 'v1'}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 4,
            right: 6,
            height: 18,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.02em',
            backgroundColor: schemaVersion >= 2 ? 'rgba(33,150,243,0.15)' : 'rgba(0,0,0,0.08)',
            color: schemaVersion >= 2 ? '#1565c0' : 'text.secondary',
            border: 'none',
            '& .MuiChip-label': { px: '5px' },
          }}
        />
      </NodeHeader>

      {/* Config panel content — hidden when zoomed out (kept mounted to preserve state) */}
      <Collapse in={!collapsed} timeout={200} sx={{ position: 'relative' }}>
        {children}
        {isAiActive && <div className="ai-active-overlay" />}
      </Collapse>
    </NodeCard>
  );
};

export default React.memo(InlineNodeView);
